import * as Yup from 'yup'
import { imageInputSchema, imageNullInputSchema } from './common'
const loginValidationSchema = Yup.object({
  username: Yup.string().required('Username is required'),
  password: Yup.string().required('Password is required'),
})
const otpSendValidationSchema = Yup.object({
  phoneNo: Yup.number().required('Phone number is required'),
  countryCode: Yup.string().required('Country code is required'),
})
const otpVerifyValidationSchema = Yup.object({
  otp: Yup.string().min(6).required('OTP is required'),
})

const resetPasswordValidationSchema = Yup.object({
  newPassword: Yup.string().required('New Password is required'),
  confirmPassword: Yup.string()
    .required('Confirm password is required')
    .oneOf([Yup.ref('newPassword')], 'Passwords must match'),
})

const userEmailValidationsSchema = Yup.object({
  email: Yup.string()
    .email('Invalid email format')
    .required('Email is required'),
})

const roleValidationSchema = Yup.object({
  role: Yup.string().required('Role is required'),
})

const createValidationSchema = Yup.object({
  name: Yup.string().required('Full Name is required'),
  phoneNo: Yup.number().required('Mobile Number is required'),
  email: Yup.string().email().required('Email is required'),
  address: Yup.string().required('Address is required'),
  password: Yup.string().optional(),
})

const updateValidationSchema = Yup.object({
  name: Yup.string().required('Full Name is required'),
  phoneNo: Yup.number().required('Mobile Number is required'),
  email: Yup.string().email().required('Email is required'),
  address: Yup.string().required('Address is required'),
})

const registerValidationSchema = Yup.object({
  name: Yup.string().required('Full Name is required'),
  phoneNo: Yup.number().required('Mobile Number is required'),
  email: Yup.string().email().required('Email is required'),
  address: Yup.string().required('Address is required'),
  password: Yup.string().required('Password is required'),
  confirmPassword: Yup.string()
    .required('Confirm password is required')
    .oneOf([Yup.ref('password')], 'Passwords must match'),
})

const identificationValidationSchema = Yup.object({
  name: Yup.string().required('Document Name is required'),
  number: Yup.string().notRequired(),
  image: imageNullInputSchema,
})

const licenseValidationSchema = Yup.object({
  name: Yup.string().required('Name is required'),
  image: imageInputSchema,
})

const patientStep2ValidationSchema = Yup.object({
  gender: Yup.string().required('Gender is required'),
  bloodGroup: Yup.string().required('Blood group is required'),
  age: Yup.number().required('Age is required'),
  symptoms: Yup.array()
    .of(Yup.number().required('Problems field is required'))
    .required('Problems field is required')
    .min(1, 'At least one problem is required'),
  avatar: imageNullInputSchema,
})

const providerValidationSchema = Yup.object({
  gender: Yup.string().required('Gender is required'),
  avatar: imageNullInputSchema,
  coverImage: imageNullInputSchema,
  yos: Yup.number()
    .typeError('Year of service must be a number')
    .required('Year of service is required'),
  qualification: Yup.string().required('Qualification is required'),
  specializations: Yup.array()
    .of(Yup.object().required('Specialization is required'))
    .required('Specialization is required'),
  category: Yup.string().required('Category is required'),
  staffRole: Yup.string().required('Role is required'),
})

const doctorStep2ValidationSchema = Yup.object({
  gender: Yup.string().required('Gender is required'),
  departments: Yup.number()
    .typeError('Departments must be a number')
    .required('Department is required'),
  qualification: Yup.string().required('Qualification is required'),
  yos: Yup.number()
    .typeError('Year of service must be a number')
    .required('Year of service is required'),
  specializations: Yup.array()
    .of(Yup.object().required('Specialization is required'))
    .required('Specialization is required'),
  avatar: imageNullInputSchema,
  coverImage: imageNullInputSchema,
  age: Yup.number().required('Age is required'),
  fees: Yup.number()
    .min(0, 'Fees must be 0 or more')
    .required('Fees is required'),
  appointmentType: Yup.string().required('Appointment Type is required'),
})

const institutionStep2ValidationSchema = Yup.object({
  departments: Yup.array()
    .of(
      Yup.number()
        .typeError('Departments is required')
        .required('Each item is required'),
    )
    .min(1, 'At least one department is required')
    .required('Departments is required'),
  specializations: Yup.array()
    .of(Yup.object().required('Specialization is required'))
    .required('Specialization is required'),
  yos: Yup.number()
    .typeError('Year of service must be a number')
    .required('Year of service is required'),
  serviceIncludes: Yup.array()
    .of(Yup.string().required('Service Provided is required'))
    .required('Service Provided is required'),
  avatar: imageNullInputSchema,
  coverImage: imageNullInputSchema,
  instituteType: Yup.string().required('Institute Type is required'),
})

const identificationsValidationSchema = Yup.object({
  documents: Yup.array()
    .of(identificationValidationSchema)
    .min(1)
    .required('Identifications is required'),
})

const licensesValidationSchema = Yup.object({
  documents: Yup.array()
    .of(licenseValidationSchema)
    .min(1)
    .required('Medica Licence is required'),
})

const signatureValidationSchema = Yup.object({
  signature: imageInputSchema,
})

const passwordChangeValidationSchema = Yup.object().shape({
  currentPassword: Yup.string().required('Current password is required'),
  newPassword: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/[0-9]/, 'Password must contain at least one digit')
    .matches(
      /[@$!%*?&]/,
      'Password must contain at least one special character (@, $, !, %, *, ?, &)',
    )
    .notOneOf(
      [Yup.ref('currentPassword')],
      'New password cannot be the same as the current password',
    )
    .required('New password is required'),
  confirmPassword: Yup.string()
    .required('Confirm password is required')
    .oneOf([Yup.ref('newPassword')], 'Passwords must match'),
})

const linkBankAccountValidationSchema = Yup.object().shape({
  bankName: Yup.string().required('Bank name is required'),
  branchLocation: Yup.string().required('Branch Location is required'),
  accountHolderName: Yup.string().required('Account Holder Name is required'),
  accountNo: Yup.string().required('Account No is required'),
  mobileNo: Yup.string().required('Mobile No is required'),
})
const queueValidationSchema = Yup.object().shape({
  title: Yup.string().required('Title is required'),
  noOfPatient: Yup.number().required('No of Patient is required'),
  downtime: Yup.number().required('Downtime  is required'),
})
export default {
  loginValidationSchema,
  otpSendValidationSchema,
  otpVerifyValidationSchema,
  userEmailValidationsSchema,
  resetPasswordValidationSchema,
  roleValidationSchema,
  createValidationSchema,
  updateValidationSchema,
  registerValidationSchema,
  patientStep2ValidationSchema,
  providerValidationSchema,
  doctorStep2ValidationSchema,
  institutionStep2ValidationSchema,
  identificationsValidationSchema,
  licensesValidationSchema,
  signatureValidationSchema,
  passwordChangeValidationSchema,
  linkBankAccountValidationSchema,
  queueValidationSchema,
}
